import React, { useState, useEffect, useMemo, useRef } from 'react'; 
import PocketBase from 'pocketbase';
import Header from '../Header'; 
import Footer from '../Footer'; 

const pb = new PocketBase('https://bahcarplaza.com');

function BuyUsedCar({ view, setView, searchTerm, setSearchTerm, cityFilter, setCityFilter, setActiveTab, setSelectedCar }) {
  const [cars, setCars] = useState([]);
  
  const [sortBy, setSortBy] = useState('Relevance');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 7000000 });
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [yearRange, setYearRange] = useState({ min: 2000, max: 2026 });
  
  const [kmDrivenFilters, setKmDrivenFilters] = useState([]); 
  const [fuelFilters, setFuelFilters] = useState([]);
  const [transmissionFilters, setTransmissionFilters] = useState([]);
  const [colorFilters, setColorFilters] = useState([]);
  const [seatFilters, setSeatFilters] = useState([]);
  const [ownerFilters, setOwnerFilters] = useState([]);

  const [cardImageIndex, setCardImageIndex] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = (e, carId, totalImages) => {
    if (totalImages <= 1) return; 

    const swipeThreshold = 40; 
    if (touchStartX.current - touchEndX.current > swipeThreshold) {
      handleCardImageChange({ stopPropagation: () => {} }, carId, totalImages, 1);
    }
    if (touchEndX.current - touchStartX.current > swipeThreshold) {
      handleCardImageChange({ stopPropagation: () => {} }, carId, totalImages, -1);
    }
    
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  useEffect(() => {
    const handlePopState = (e) => {
      if (!e.state || !e.state.filterOpen) {
        setIsFiltersOpen(false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const openFilters = () => {
    setIsFiltersOpen(true);
    window.history.pushState({ page: 'buy', filterOpen: true }, '');
  };

  const closeFilters = () => {
    setIsFiltersOpen(false);
    if (window.history.state && window.history.state.filterOpen) {
      window.history.back();
    }
  };

  useEffect(() => {
    const fetchCars = async () => {
      try { 
        const records = await pb.collection('cars').getFullList({ expand: 'added_by', sort: '-created' }); 
        setCars(records); 
      } catch (e) { console.error(e); }
    };
    fetchCars();
  }, []);

  const getVal = (val) => (Array.isArray(val) ? val[0] : val || "");
  const getImages = (val) => Array.isArray(val) ? val : (val ? [val] : []);

  const handleCardImageChange = (e, carId, total, direction) => {
    if (e && e.stopPropagation) e.stopPropagation(); 
    setCardImageIndex(prev => {
      const current = prev[carId] || 0;
      return { ...prev, [carId]: (current + direction + total) % total };
    });
  };

  const filteredCars = useMemo(() => {
    // 🚨 FIXED: Show ONLY 'Approved' cars, hide 'Sold' 🚨
    let result = cars.filter(car => car.status === 'Approved');

    if (searchTerm) result = result.filter(c => c.brand.toLowerCase().includes(searchTerm.toLowerCase()) || c.model.toLowerCase().includes(searchTerm.toLowerCase()));
    if (cityFilter) result = result.filter(c => getVal(c.city) === cityFilter);

    result = result.filter(c => c.price >= priceRange.min && c.price <= priceRange.max);
    if (selectedBrands.length > 0) result = result.filter(c => selectedBrands.includes(c.brand));
    result = result.filter(c => c.year >= yearRange.min && c.year <= yearRange.max);

    if (kmDrivenFilters.length > 0) {
      result = result.filter(c => {
        const km = c.kilometers_driven;
        return kmDrivenFilters.some(filterVal => {
          if (filterVal === '3000') return km <= 3000;
          if (filterVal === '10000') return km > 3000 && km <= 10000;
          if (filterVal === '30000') return km > 10000 && km <= 30000;
          if (filterVal === '70000') return km > 30000 && km <= 70000;
          if (filterVal === '120000') return km > 70000 && km <= 120000;
          if (filterVal === 'above') return km > 120000;
          return false;
        });
      });
    }

    if (fuelFilters.length > 0) result = result.filter(c => fuelFilters.includes(getVal(c.fuel_type)));
    if (transmissionFilters.length > 0) result = result.filter(c => transmissionFilters.includes(getVal(c.transmission)));
    if (colorFilters.length > 0) result = result.filter(c => colorFilters.includes(getVal(c.color)));
    if (seatFilters.length > 0) result = result.filter(c => seatFilters.includes(getVal(c.seats)));
    if (ownerFilters.length > 0) result = result.filter(c => ownerFilters.includes(c.owner_type));

    if (sortBy === 'Price - Low to High') result.sort((a, b) => a.price - b.price);
    if (sortBy === 'Price - High to Low') result.sort((a, b) => b.price - a.price);
    if (sortBy === 'KM Driven - Low to High') result.sort((a, b) => a.kilometers_driven - b.kilometers_driven);
    if (sortBy === 'Newest First') result.sort((a, b) => b.year - a.year);

    return result;
  }, [cars, searchTerm, cityFilter, priceRange, selectedBrands, yearRange, kmDrivenFilters, fuelFilters, transmissionFilters, colorFilters, seatFilters, ownerFilters, sortBy]);

  const allAvailableBrands = [...new Set(cars.map(c => c.brand))];
  const allAvailableColors = {
    'Red': '#ff0000', 'Blue': '#0000ff', 'Black': '#000000', 'White': '#ffffff', 'Silver': '#c0c0c0', 
    'Gray': '#808080', 'Maroon': '#800000', 'Orange': '#ffa500', 'Yellow': '#ffff00', 'Golden': '#ffd700', 'Green': '#008000', 'Brown': '#a52a2a'
  };

  const handleDirectBuy = async (carToBuy) => {
    // 🚨 FIXED: Check for 'Sold' status 🚨
    if (carToBuy.status === 'Sold') return; 
    
    if (!pb.authStore.isValid) { 
      alert("Please login first to proceed to checkout."); 
      setView('auth'); 
      return; 
    }
    setSelectedCar(carToBuy);
    setView('checkout_page');
  };

  const handleAddToWishlist = async (carId) => {
    if (!pb.authStore.isValid) return alert("Please log in to add to wishlist.");
    try { 
      await pb.collection('wishlist').create({ user_id: pb.authStore.model?.id, car_id: carId }); 
      alert("Added to Wishlist!"); 
    } catch (e) { alert("Already in wishlist or error occurred."); }
  };

  return (
    <div style={{ backgroundColor: '#f9fafb', minHeight: '100vh', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      
      <Header view={view} setView={setView} setSearchTerm={setSearchTerm} setCityFilter={setCityFilter} setActiveTab={setActiveTab} />
      
      <button 
        onClick={() => isFiltersOpen ? closeFilters() : openFilters()}
        className="md:hidden fixed left-0 top-1/2 -translate-y-1/2 bg-[#002f34] text-white p-3 rounded-r-xl shadow-[0_5px_15px_rgba(0,0,0,0.3)] z-[90] flex items-center justify-center transition-all cursor-pointer border-none"
        title="Toggle Filters"
      >
        <svg width="8" height="18" viewBox="6 0 12 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isFiltersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }}>
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>

      <div className="max-w-[1600px] w-full mx-auto flex flex-col md:flex-row gap-8 p-4 md:p-8" style={{ flex: 1 }}>
        
        <aside className={`
          md:w-[320px] md:flex-shrink-0 md:bg-white md:p-6 md:rounded-[20px] md:h-fit md:sticky md:top-[100px] md:shadow-[0_4px_20px_rgba(0,0,0,0.05)] md:block md:translate-x-0
          fixed inset-0 bg-white z-[100] p-6 overflow-y-auto transition-transform duration-300 ease-in-out w-full h-full
          ${isFiltersOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          
          <div className="flex justify-between items-center mb-6 md:hidden border-b-2 border-[#f1f1f1] pb-4">
            <h2 className="text-2xl font-black text-[#002f34] m-0">Filter Options</h2>
            <button onClick={closeFilters} className="bg-transparent border-none text-[#ef5e33] font-black text-2xl cursor-pointer">✕</button>
          </div>

          <h2 className="hidden md:block text-[20px] font-black mb-[25px] text-[#002f34] border-b-2 border-[#f1f1f1] pb-[10px]">Filters</h2>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '15px' }}>Price Range</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '900', color: '#ef5e33' }}>
              <span>₹{priceRange.min.toLocaleString()}</span>
              <span>₹{priceRange.max.toLocaleString()}</span>
            </div>
            <input type="range" min="0" max="7000000" step="50000" value={priceRange.max} onChange={(e) => setPriceRange({...priceRange, max: parseInt(e.target.value)})} style={{ width: '100%', accentColor: '#ef5e33', marginTop: '10px' }} />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '15px' }}>Brands</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {allAvailableBrands.map(brand => (
                <button key={brand} onClick={() => setSelectedBrands(prev => prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand])}
                  style={{ padding: '6px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', border: '1px solid #eee', cursor: 'pointer', backgroundColor: selectedBrands.includes(brand) ? '#002f34' : '#fff', color: selectedBrands.includes(brand) ? '#fff' : '#555' }}>
                  {brand}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '10px' }}>Model Year (2000 - Today)</p>
            <input type="range" min="2000" max="2026" value={yearRange.min} onChange={(e) => setYearRange({...yearRange, min: parseInt(e.target.value)})} style={{ width: '100%', accentColor: '#002f34' }} />
            <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 'bold', marginTop: '5px' }}>From: {yearRange.min}</div>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '12px' }}>Kms Driven</p>
            {[
              { label: '0 - 3000 kms', val: '3000' },
              { label: '3000 - 10,000 kms', val: '10000' },
              { label: '10,000 - 30,000 kms', val: '30000' },
              { label: '30,000 - 70,000 kms', val: '70000' },
              { label: '70,000 - 1,20,000 kms', val: '120000' },
              { label: '1,20,000 Above', val: 'above' }
            ].map(km => (
              <label key={km.val} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" onChange={(e) => setKmDrivenFilters(prev => e.target.checked ? [...prev, km.val] : prev.filter(v => v !== km.val))} style={{ accentColor: '#ef5e33', width: '16px', height: '16px', cursor: 'pointer' }} /> {km.label}
              </label>
            ))}
          </div>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '12px' }}>Fuel Type</p>
            {['Petrol', 'Disel', 'Electric', 'CNG'].map(fuel => (
              <label key={fuel} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" onChange={(e) => setFuelFilters(prev => e.target.checked ? [...prev, fuel] : prev.filter(f => f !== fuel))} style={{ accentColor: '#ef5e33', width: '16px', height: '16px', cursor: 'pointer' }} /> {fuel}
              </label>
            ))}
          </div>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '12px' }}>Transmission</p>
            {['Manual', 'Automatic'].map(tr => (
              <label key={tr} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" onChange={(e) => setTransmissionFilters(prev => e.target.checked ? [...prev, tr] : prev.filter(t => t !== tr))} style={{ accentColor: '#ef5e33', width: '16px', height: '16px', cursor: 'pointer' }} /> {tr}
              </label>
            ))}
          </div>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '12px' }}>Color</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {Object.keys(allAvailableColors).map(color => (
                <div key={color} onClick={() => setColorFilters(prev => prev.includes(color) ? prev.filter(c => c !== color) : [...prev, color])}
                  style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: allAvailableColors[color], cursor: 'pointer', border: colorFilters.includes(color) ? '4px solid #ef5e33' : '1px solid #ddd', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }} title={color} />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '12px' }}>Seats</p>
            {['4 Seater', '5 Seater', '6 Seater', '7 Seater', '9 Plus Seater'].map(seat => (
              <label key={seat} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" onChange={(e) => setSeatFilters(prev => e.target.checked ? [...prev, seat] : prev.filter(s => s !== seat))} style={{ accentColor: '#ef5e33', width: '16px', height: '16px', cursor: 'pointer' }} /> {seat}
              </label>
            ))}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '12px' }}>Owner Type</p>
            {['1st Hand', '2nd Hand', '3rd Hand', 'More Than 3'].map(owner => (
              <label key={owner} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', marginBottom: '8px', cursor: 'pointer', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '10px', border: '1px solid #eee' }}>
                <input type="checkbox" onChange={(e) => setOwnerFilters(prev => e.target.checked ? [...prev, owner] : prev.filter(o => o !== owner))} style={{ accentColor: '#ef5e33', width: '16px', height: '16px', cursor: 'pointer' }} />
                <span style={{ fontWeight: 'bold', color: '#444' }}>{owner}</span>
              </label>
            ))}
          </div>
          
          <button onClick={closeFilters} className="md:hidden w-full mt-4 bg-[#002f34] text-white p-4 rounded-xl font-black text-sm uppercase shadow-lg border-none cursor-pointer">
            Apply Filters
          </button>
        </aside>

        <main style={{ flex: 1 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h1 className="text-2xl md:text-[28px] font-black text-[#002f34] m-0">{filteredCars.length} Cars Found</h1>
            
            <div className="flex items-center gap-2 md:gap-[10px]">
              <span className="text-xs md:text-[14px] font-bold text-[#888]">Sort By:</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="p-2 md:p-[10px_20px] rounded-xl md:rounded-[12px] border border-[#ddd] font-bold outline-none cursor-pointer text-xs md:text-sm">
                <option>Relevance</option>
                <option>Price - Low to High</option>
                <option>Price - High to Low</option>
                <option>KM Driven - Low to High</option>
                <option>Newest First</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredCars.map((car) => {
              
              const images = getImages(car.image);
              const currentIndex = cardImageIndex[car.id] || 0;
              // 🚨 FIXED: Check for 'Sold' status 🚨
              const isSold = car.status === 'Sold'; 

              return (
                <div 
                  key={car.id} 
                  onClick={() => { setSelectedCar(car); setView('details'); }} 
                  className={`bg-white p-3 rounded-[1.5rem] md:rounded-[2rem] shadow-md hover:shadow-2xl transition-all duration-300 group relative flex flex-col ${isSold ? 'opacity-80 grayscale-[20%]' : 'cursor-pointer hover:-translate-y-2'}`}
                >
                  <div 
                    className="h-40 md:h-44 rounded-[1rem] md:rounded-[1.5rem] mb-3 overflow-hidden relative group/slider"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={(e) => handleTouchEnd(e, car.id, images.length)}
                  >
                    <img src={`https://bahcarplaza.com/api/files/${car.collectionId}/${car.id}/${images[currentIndex]}`} className="w-full h-full object-cover group-hover:scale-110 transition duration-700" alt="car" />
                    
                    {/* 🚨 SOLD OUT OVERLAY 🚨 */}
                    {isSold && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20">
                        <span className="bg-red-600 text-white font-black px-4 py-1.5 rounded-lg border-2 border-white shadow-xl transform -rotate-12 text-lg uppercase tracking-widest">Sold Out</span>
                      </div>
                    )}

                    <button onClick={(e) => { e.stopPropagation(); handleAddToWishlist(car.id); }} className="absolute top-3 right-3 bg-transparent border-none cursor-pointer z-20 transition-transform text-xl md:text-2xl hover:scale-110 drop-shadow-md">
                      ❤️
                    </button>

                    {images.length > 1 && !isSold && (
                      <>
                        <button onClick={(e) => handleCardImageChange(e, car.id, images.length, -1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover/slider:opacity-100 transition-opacity z-10 hover:bg-[#ef5e33] text-xs border-none cursor-pointer shadow-lg">❮</button>
                        <button onClick={(e) => handleCardImageChange(e, car.id, images.length, 1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover/slider:opacity-100 transition-opacity z-10 hover:bg-[#ef5e33] text-xs border-none cursor-pointer shadow-lg">❯</button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10 bg-black/20 px-2 py-1 rounded-full backdrop-blur-sm">
                            {images.map((_, idx) => (
                              <div key={idx} className={`h-1.5 rounded-full transition-all ${currentIndex === idx ? 'w-3 bg-[#ef5e33]' : 'w-1.5 bg-white/70'}`} />
                            ))}
                        </div>
                      </>
                    )}

                  </div>
                  <h3 className="text-base md:text-lg font-black text-[#002f34] uppercase px-2 truncate mb-2">{car.brand} {car.model}</h3>
                  <div className="flex flex-wrap gap-1.5 md:gap-2 px-2 mb-4">
                    {car.year && <span className="bg-[#f4f7f6] text-[#555] text-[8px] md:text-[9px] font-extrabold px-2 py-1 rounded-md uppercase tracking-wider">{car.year}</span>}
                    {car.kilometers_driven && <span className="bg-[#f4f7f6] text-[#555] text-[8px] md:text-[9px] font-extrabold px-2 py-1 rounded-md uppercase tracking-wider">{car.kilometers_driven?.toLocaleString()} KM</span>}
                    {getVal(car.fuel_type) && <span className="bg-[#f4f7f6] text-[#555] text-[8px] md:text-[9px] font-extrabold px-2 py-1 rounded-md uppercase tracking-wider">{getVal(car.fuel_type)}</span>}
                    {getVal(car.transmission) && <span className="bg-[#f4f7f6] text-[#555] text-[8px] md:text-[9px] font-extrabold px-2 py-1 rounded-md uppercase tracking-wider">{getVal(car.transmission)}</span>}
                  </div>
                  <div className="flex justify-between items-center px-2 mt-auto pb-1">
                    <div className="flex flex-col">
                      <span className="text-[9px] md:text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Price</span>
                      <span className="text-lg md:text-xl font-black text-[#002f34] leading-none">₹{car.price?.toLocaleString()}</span>
                    </div>
                    <div className="flex gap-2">
                      {/* 🚨 DYNAMIC BUTTON 🚨 */}
                      {isSold ? (
                        <button disabled className="bg-gray-300 text-gray-500 font-black text-[10px] md:text-[11px] uppercase px-3 py-1.5 md:px-4 md:py-2 rounded-xl shadow-none border-none cursor-not-allowed">SOLD</button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); handleDirectBuy(car); }} className="bg-[#002f34] text-white font-black text-[10px] md:text-[11px] uppercase px-3 py-1.5 md:px-4 md:py-2 rounded-xl hover:bg-[#ef5e33] transition-colors shadow-lg border-none cursor-pointer">BUY</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filteredCars.length === 0 && <div className="text-center p-20 font-bold text-gray-400">No cars match your filters.</div>}
        </main>
      </div>

      <Footer setView={setView} />
      
    </div>
  );
}

export default BuyUsedCar;